import close from "../../../assets/imgs/home/bg_x.png"

import React, { useState } from "react"
import StyledModal, { ModalProps } from "../../StyledModal"
import { useTranslation } from "next-i18next"
import { getResultText, tryGetErrorMessage } from "../../../utils/string"
import { formatNumber, parseEther } from "../../../utils/number"
import { declineInvitation } from "../../../services/games"
import useBettingContract from "../../../hooks/useBettingContract"
import { getCreateSignature } from "../../../services/security"
import { useMatchMutate } from "../../../hooks/useMatchMutate"
import { useRouter } from "next/router"
import useGlobalStore from "../../../hooks/useGlobalStore"
import { Loading } from "../../shared/Loading"
import { BigNumber } from "ethers"
import useTokenAllowance from "../../../hooks/useTokenAllowance"
import useTokenApprove from "../../../hooks/useTokenApprove"
import useTokenContract from "../../../hooks/useTokenContract"
import { GameSyncTypeEnum, syncGame } from "../../../services/syncGame"
import { getTokenAddress } from "../../../utils/network"
import { useAccount } from "wagmi"
import useBBBNetwork from "../../../hooks/useBBBNetwork"

interface AcceptDebateModalProps extends ModalProps {
    gameDetails: any

    onAccepted: () => void
}

export const AcceptDebateModal = ({ gameDetails, isOpen, onRequestClose, onAccepted }: AcceptDebateModalProps) => {
    const router = useRouter()

    const { setPopupMessage } = useGlobalStore()
    const [isLoading, setIsLoading] = useState(false)

    const { t: tCommon } = useTranslation("common")
    const { t: tGameDetails } = useTranslation("game-details")
    const { address: account } = useAccount()
    const {
        chain: { id: chainId }
    } = useBBBNetwork()

    const mutate = useMatchMutate()

    const {
        id,
        uid,
        title,
        hostStake,
        hostBet,
        selections,
        competitor,
        contractAddress,
        expired,
        counterpartOdds,
        hostOdds,
        owner,
        judgementType,
        intermediary,
        tokenName,
        chainId: gameChainId
    } = gameDetails
    const { choice: hostChoice } = hostBet
    const { walletAddress: creator } = owner
    const { initBet: competitorInitBet } = competitor || {}

    const { walletAddress: intermediaryWalletAddress } = intermediary || {}

    const bettingContract = useBettingContract(contractAddress, chainId)
    const tokenAddress = getTokenAddress(tokenName, chainId)
    const tokenContract = useTokenContract(tokenAddress)

    const allowance = useTokenAllowance(account, tokenContract, bettingContract)
    const { onApprove } = useTokenApprove(tokenContract, bettingContract)

    const onHandleApprove = async (): Promise<boolean> => {
        let isApproved = false

        try {
            setIsLoading(true)

            const approveAmount = parseEther(gameChainId, tokenName, competitorInitBet)
            if (BigNumber.from(allowance).lt(approveAmount)) {
                const response = await onApprove(approveAmount)
                if (response) {
                    isApproved = true

                    await response.wait()
                }
            } else {
                isApproved = true
            }

            setIsLoading(false)
        } catch (ex: any) {
            if (ex.code !== 4001) {
                setPopupMessage({ message: tryGetErrorMessage(ex) })
            }
        } finally {
            setIsLoading(false)
        }

        return isApproved
    }

    const onAccept = async () => {
        const isApproved = await onHandleApprove()
        if (!isApproved) {
            return
        }

        try {
            setIsLoading(true)

            const signatureResponse = await getCreateSignature(uid, judgementType)
            const signature = signatureResponse.data.data.signature

            const response = await bettingContract.createGame(
                uid,
                creator,
                [parseEther(gameChainId, tokenName, hostStake), parseEther(gameChainId, tokenName, competitorInitBet)],
                hostChoice,
                expired,
                judgementType,
                tokenAddress,
                hostOdds,
                counterpartOdds,
                intermediaryWalletAddress || "0x0000000000000000000000000000000000000000",
                signature
            )

            const transaction = await response.wait()

            await syncGame(GameSyncTypeEnum.CREATE, transaction.transactionHash, uid)

            await mutate(/NOTIFICATIONS/)

            onRequestClose()

            onAccepted()
        } catch (ex: any) {
            if (ex.code !== 4001) {
                setPopupMessage({ message: tryGetErrorMessage(ex) })
            }
        } finally {
            setIsLoading(false)
        }
    }

    const onDecline = async () => {
        try {
            setIsLoading(true)

            await declineInvitation(id)

            onRequestClose()

            await mutate(/NOTIFICATIONS/)

            await router.push("/")
        } catch (ex: any) {
            if (ex.code !== 4001) {
                setPopupMessage({ message: tryGetErrorMessage(ex) })
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <StyledModal isOpen={isOpen} onRequestClose={onRequestClose}>
            {isLoading && <Loading />}
            <div className="m-auto w-[94%] rounded-[5px] bg-[url(../assets/imgs/bg/accept-debate.png)] bg-[length:100%_100%] bg-center bg-no-repeat pb-9 text-center lg:w-[582px]">
                <div className="pt-[9px] text-right">
                    <img
                        src={close.src}
                        onClick={onRequestClose}
                        className="float-right mr-[12px] w-[30px] hover:opacity-80"
                    />
                </div>
                <div className={"mt-[28px] px-6 text-left text-2xl font-bold leading-9 text-neutral-1-600 lg:px-12"}>
                    {title}
                </div>
                <div
                    className={
                        "mx-6 mt-4 h-[138px] rounded-[5px] border border-black bg-[#DCDEE7] px-[15px] pt-[22px] text-lg1-1 font-normal leading-[23px] text-[#05050F] lg:mx-12"
                    }
                >
                    <div className={"flex items-center justify-between"}>
                        <div>{tGameDetails("acceptDebate.hostOpinion")}</div>
                        <div>{tCommon(getResultText(hostChoice, selections))}</div>
                    </div>
                    <div className={"mt-[13px] flex items-center justify-between"}>
                        <div>{tGameDetails("acceptDebate.hostStake")}</div>
                        <div>{formatNumber(hostStake)} BUSD</div>
                    </div>
                    <div className={"mt-[13px] flex items-center justify-between"}>
                        <div>{tGameDetails("acceptDebate.willPay")}</div>
                        <div>{formatNumber(competitorInitBet)} BUSD</div>
                    </div>
                </div>
                <div className={"mt-4 px-6 text-left font-secondary text-base font-medium text-neutral-1-600 lg:px-12"}>
                    {tGameDetails("acceptDebate.confirmMessage")}
                </div>
                <div className={"b-flex-center mt-[29px]"}>
                    <button
                        onClick={onAccept}
                        className={
                            "b-flex-center mr-4 h-[40px] w-[150px] rounded-[4px] bg-accent-1 text-lg1-1 font-bold text-neutral-1 hover:text-primary lg:w-[185px]"
                        }
                    >
                        {tCommon("button.yes")}
                    </button>
                    <button
                        onClick={onDecline}
                        className={
                            "b-flex-center h-[40px] w-[150px] rounded-[4px] border border-[1.6px] border-neutral-1-500 text-lg1-1 font-bold text-neutral-1 hover:text-primary lg:w-[185px]"
                        }
                    >
                        {tCommon("button.no")}
                    </button>
                </div>
            </div>
        </StyledModal>
    )
}
